/* eslint-disable */
import { Shield, Settings, Trash2, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

const UsersTable = ({
  users,
  products,
  getRoleInfo,
  onOpenPermissions,
  onDeleteUser,
  countActivePermissions,
}) => {
  const [deletingId, setDeletingId] = useState(null);
  const { t, i18n } = useTranslation();

  const getProductLabel = (productId) => {
    const product = products?.find((p) => p.id === productId);
    if (!product) return productId;

    const noName = t("workersManagement.table.noNameProduct");

    if (product?.sku) return `${product.name || noName}`;
    return product?.name || noName;
  };

  const handleDelete = async (userId) => {
    try {
      setDeletingId(userId);
      await onDeleteUser(userId);
    } finally {
      setDeletingId(null);
    }
  };

  function formatFirestoreTimestamp(timestamp) {
    if (!timestamp?._seconds) return "";

    const date = new Date(timestamp._seconds * 1000);

    // ✅ locale dynamique selon la langue
    const locale =
      i18n.language === "ar" ? "ar" : i18n.language === "en" ? "en-US" : "fr-FR";

    return date.toLocaleString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  return (
    <div className="bg-white dark:!bg-[#181819] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-0 mb-6 overflow-hidden">
      <table className="w-full table-auto text-sm text-left">
        <thead className="bg-[#fcf6ff] dark:!bg-[#1e1e1e] text-[#9e68ba] font-medium border-b border-[#fbf4ff] dark:!border-[#3c3a3d]">
          <tr>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.user")}
            </th>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.email")}
            </th>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.role")}
            </th>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.confirmerProducts")}
            </th>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.permissions")}
            </th>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.createdAt")}
            </th>
            <th className="py-3 px-6 text-center">
              {t("workersManagement.table.actions")}
            </th>
          </tr>
        </thead>

        <tbody className="text-gray-600 divide-y dark:divide-[#3c3a3d]">
          {users.map((user) => {
            const roleInfo = getRoleInfo(user.role);
            const isDeleting = deletingId === user.id;

            const permsCount = countActivePermissions(user);

            return (
              <tr
                key={user.id}
                className="hover:bg-[#fcf6ff] dark:hover:bg-[#1e1e1e] transition-colors"
              >
                <td className="flex items-center justify-center gap-x-3 py-3 px-6 whitespace-nowrap">
                  {user.photoUrl ? (
                    <img
                      src={user.photoUrl}
                      alt={user.name}
                      className="rounded-lg overflow-hidden shadow-sm border-2 border-white w-10 h-10 object-cover"
                    />
                  ) : (
                    <div className="rounded-lg overflow-hidden shadow-sm border-2 border-white w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
                      {user.email?.charAt(0) ||
                        t("workersManagement.table.userInitialFallback")}
                    </div>
                  )}
                  <div className="text-center">
                    <span className="block text-gray-800 text-sm font-medium dark:text-gray-200">
                      {user.name}
                    </span>
                  </div>
                </td>

                <td className="py-3 px-4 text-center whitespace-nowrap dark:text-gray-200">
                  {user.email}
                </td>

                <td className="py-3 px-4 text-center whitespace-nowrap">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}
                  >
                    {roleInfo.label}
                  </span>
                </td>

                <td className="py-3 px-4 text-center">
                  {user.role === "confirmer" &&
                  Array.isArray(user.products) &&
                  user.products.length > 0 ? (
                    user.products.length >= products?.length ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium border border-purple-200 dark:!border-purple-700">
                        {t("workersManagement.table.allProductsSelected")}
                      </span>
                    ) : (
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {user.products.map((productId) => (
                          <span
                            key={productId}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:!border-purple-700"
                          >
                            {getProductLabel(productId)}
                          </span>
                        ))}
                      </div>
                    )
                  ) : (
                    <span className="text-xs text-gray-400">
                      {t("workersManagement.table.dash")}
                    </span>
                  )}
                </td>

                <td className="py-3 px-4 text-center whitespace-nowrap">
                  <div className="flex items-center justify-center gap-2">
                    <Shield className="w-4 h-4 text-[#9e68ba]" />
                    <span className="text-sm text-gray-900 dark:text-gray-200">
                      {permsCount}{" "}
                      {t("workersManagement.table.permissionCount", {
                        count: permsCount,
                      })}
                    </span>
                  </div>
                </td>

                <td className="py-3 px-4 text-center whitespace-nowrap dark:text-gray-200">
                  {formatFirestoreTimestamp(user.createdAt)}
                </td>

                <td className="px-6 py-3 text-center">
                  <div className="flex justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenPermissions(user)}
                      className="p-1 rounded-full hover:bg-[#faf1ff] dark:hover:bg-[#2a2a2a] transition-colors"
                      title={t("workersManagement.table.managePermissions")}
                    >
                      <Settings
                        size={20}
                        className="text-[#9e68ba] dark:text-[#b57ed8]"
                      />
                    </button>

                    {user.role !== "store_owner" && (
                      <button
                        type="button"
                        onClick={() => handleDelete(user.id)}
                        disabled={isDeleting}
                        className="p-1 rounded-full hover:bg-[#faf1ff] dark:hover:bg-[#2a2a2a] transition-colors"
                        title={t("workersManagement.table.deleteUser")}
                      >
                        {isDeleting ? (
                          <Loader2
                            size={20}
                            className="text-[#9e68ba] animate-spin"
                          />
                        ) : (
                          <Trash2 size={20} className="text-red-600" />
                        )}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;
